import { Component, OnDestroy, OnInit, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProfessorTheme, User } from 'src/app/_model/models';
import { ThemeService, PRESET_THEMES } from 'src/app/_services/theme.service';
import { AuthService } from 'src/app/_services/auth.service';
import { unlockPremiumComponent } from 'src/app/modules/dashboard/components/unlockPremium/unlockPremium.component';


@Component({
  selector: 'tangram-theme-customizer-modal',
  templateUrl: './theme-customizer-modal.component.html',
  styleUrls: ['./theme-customizer-modal.component.scss'],
})
export class ThemeCustomizerModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('previewRef', { static: false }) previewRef: ElementRef<HTMLElement>;
  /** Passed in by the caller — the palette to edit. */
  theme: Partial<ProfessorTheme> = {};

  /** Passed in by the caller — existing theme ID (empty = create new). */
  themeId: string = '';

  /** Local working copy so we can cancel without side-effects. */
  colors: Partial<ProfessorTheme> = {};

  /** Name used when saving/updating. */
  themeName: string = '';

  presets = Object.values(PRESET_THEMES);
  selectedPresetLabel: string = '';

  saving: boolean = false;
  saveError: string = '';

  private readonly PROFESSOR_ROLES = ['CURATOR', 'SUPER_USER', 'ADMIN'];
  get isProfessor(): boolean {
    return !!this.currentUser && this.PROFESSOR_ROLES.includes(this.currentUser.role);
  }

  /** Snapshot of CSS vars before the modal opened — restored on cancel. */
  private _snapshot: [string, string][] = [];
  /** Snapshot of data-tangram-mode before the modal opened. */
  private _modeSnapshot: string | null = null;
  /** Snapshot of data-tangram-theme before the modal opened. */
  private _themeAttrSnapshot: string | null = null;
  /** Snapshot of localStorage tangram_active_mode before the modal opened. */
  private _localStorageModeSnapshot: string | null = null;

  private currentUser: User | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    public activeModal: NgbActiveModal,
    private themeService: ThemeService,
    private authService: AuthService,
    private modalService: NgbModal,
  ) {}

  /** True when the user can save / apply (paid subscriber or super-user). */
  get hasActiveSubscription(): boolean {
    if (!this.currentUser) { return false; }
    return !!(this.currentUser as any).subscriptionActive || this.currentUser.role === 'SUPER_USER';
  }

  /**
   * Returns true when the caller may proceed with a save. Returns false and
   * pops the upgrade modal when the user is not a paid subscriber — preview
   * and customization are free, persisting the palette is the gated action.
   */
  private _ensureCanSave(): boolean {
    if (this.hasActiveSubscription) { return true; }
    this.modalService.open(unlockPremiumComponent, {
      centered: true,
      windowClass: 'unlock-premium',
    });
    return false;
  }

  /** True when editing an existing theme; false when creating a new one. */
  get isEditMode(): boolean {
    return !!this.themeId;
  }

  ngOnInit(): void {
    this.authService.currentUser
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => { this.currentUser = user; });

    // Capture current CSS var state so we can restore on cancel
    const root = document.documentElement;
    const varNames = [
      '--tangram-bg', '--tangram-surface', '--tangram-text',
      '--tangram-header-bg', '--tangram-header-text', '--tangram-primary',
      '--tangram-primary-text', '--tangram-secondary', '--tangram-accent',
      '--tangram-button', '--tangram-panel',
    ];
    this._snapshot = varNames.map(function(v) {
      return [v, root.style.getPropertyValue(v)] as [string, string];
    });
    this._modeSnapshot = root.getAttribute('data-tangram-mode');
    this._themeAttrSnapshot = root.getAttribute('data-tangram-theme');
    this._localStorageModeSnapshot = localStorage.getItem('tangram_active_mode');

    // Seed working copy from the passed-in theme (pre-fills colors on edit).
    // buttonColor is kept in sync with primaryButtonColor — the UI exposes a
    // single "Primary / Button Color" picker and both CSS vars must match.
    const primaryBtn = this.theme.primaryButtonColor || this.theme.buttonColor || '#3737CD';
    this.colors = {
      primaryButtonColor: primaryBtn,
      primaryTextColor:   this.theme.primaryTextColor   || '#FFFFFF',
      secondaryColor:     primaryBtn,
      accentColor:        this.theme.accentColor        || '#7C7CFF',
      backgroundColor:    this.theme.backgroundColor    || '#FFFFFF',
      textColor:          this.theme.textColor          || '#111111',
      buttonColor:        primaryBtn,
      headerBackground:   primaryBtn,
      headerTextColor:    this.theme.headerTextColor    || '#FFFFFF',
      surfaceColor:       this.theme.surfaceColor       || '#F5F5F5',
      cardBackground:     this.theme.cardBackground     || '#F5F5F5',
    };

    // Pre-fill name when editing an existing theme
    this.themeName = this.theme.name || '';

    // Apply existing colors as live preview immediately on edit
    if (this.isEditMode) {
      this.themeService.applyColorsLive(this.colors);
    }
  }

  ngAfterViewInit(): void {
    this._syncPreviewVars();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Preset selection ───────────────────────────────────────────────────────

  applyPreset(preset: typeof PRESET_THEMES[string]): void {
    this.selectedPresetLabel = preset.label;
    const palette: Partial<ProfessorTheme> = {};
    Object.keys(preset).forEach(key => {
      if (key !== 'label') { palette[key] = preset[key]; }
    });
    Object.assign(this.colors, palette);
    this.colors.buttonColor      = this.colors.primaryButtonColor;
    this.colors.headerBackground = this.colors.primaryButtonColor;
    this.colors.secondaryColor   = this.colors.primaryButtonColor;
    this.themeService.applyColorsLive(this.colors);
    this._syncPreviewVars();
  }

  // ── Live preview as user adjusts a picker ─────────────────────────────────

  onColorChange(): void {
    this.colors.buttonColor      = this.colors.primaryButtonColor;
    this.colors.headerBackground = this.colors.primaryButtonColor;
    this.colors.secondaryColor   = this.colors.primaryButtonColor;
    this.themeService.applyColorsLive(this.colors);
    this._syncPreviewVars();
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Apply = create/update the theme AND set it as the active theme for the app.
   * Colors remain applied after the modal closes (no snapshot restore).
   */
  apply(): void {
    if (!this._ensureCanSave()) { return; }
    if (this.isEditMode) {
      this._updateAndApply();
    } else {
      this._createAndApply();
    }
  }

  /**
   * Save Template = persist the palette as a reusable template but do NOT
   * change the currently active theme.  DOM is restored on close.
   * In edit mode this updates the stored template without changing the active theme.
   */
  saveTemplate(): void {
    if (!this._ensureCanSave()) { return; }
    if (this.isEditMode) {
      this._updateTheme();
    } else {
      this._createAndPersist();
    }
  }

  // ── Create flow ────────────────────────────────────────────────────────────

  private _createAndPersist(): void {
    if (this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    const name = this.themeName || this._generateDefaultName();
    const payload: Partial<ProfessorTheme> = Object.assign({}, this.colors, { name });

    this.themeService.createTheme(payload).subscribe(
      (res: any) => {
        const created = res && res.data;
        const newId = created && created._id;
        this.saving = false;
        // Restore DOM to pre-modal state: saving only persists config, it does NOT
        // auto-apply the new theme or change the currently active theme.
        this._restoreSnapshot();
        this.activeModal.close({ saveToApi: true, colors: this.colors, name, newThemeId: newId || null });
      },
      () => {
        this.saving = false;
        this.saveError = 'Failed to save theme. Please try again.';
      }
    );
  }

  // ── Create + Apply flow (Apply button in create mode) ─────────────────────

  private _createAndApply(): void {
    if (this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    const name = this.themeName || this._generateDefaultName();
    const payload: Partial<ProfessorTheme> = Object.assign({}, this.colors, { name });

    this.themeService.createTheme(payload).subscribe(
      (res: any) => {
        const created = res && res.data;
        const newId = created && created._id;

        // Keep colors live — do NOT restore snapshot so the app stays painted.
        this.themeService.applyColorsLive(this.colors);

        const closeModal = () => {
          this.saving = false;
          this.activeModal.close({ applied: true, colors: this.colors, name, newThemeId: newId || null });
        };

        if (newId) {
          const req$ = this.isProfessor
            ? this.themeService.setDefaultTheme(newId)
            : this.themeService.updatePreferences({ selectedProfessorThemeId: newId });
          req$.pipe(takeUntil(this.destroy$)).subscribe(() => { closeModal(); }, () => { closeModal(); });
        } else {
          closeModal();
        }
      },
      () => {
        this.saving = false;
        this.saveError = 'Failed to apply theme. Please try again.';
      }
    );
  }

  // ── Edit / update flow ─────────────────────────────────────────────────────

  private _updateTheme(): void {
    if (this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    const name = this.themeName || this._generateDefaultName();
    const payload: Partial<ProfessorTheme> = Object.assign({}, this.colors, { name });

    this.themeService.updateTheme(this.themeId, payload).subscribe({
      next: () => {
        this.saving = false;
        const isApplied = this.themeService.currentTheme &&
                          this.themeService.currentTheme.activeThemeId === this.themeId;
        if (isApplied) {
          // Theme is already active — keep the new colors live without needing a page refresh.
          this.themeService.applyColorsLive(this.colors);
          this.activeModal.close({ applied: true, updated: true, colors: this.colors, name, themeId: this.themeId });
        } else {
          // Theme is not active — restore DOM so saving a non-applied theme doesn't silently change the UI.
          this._restoreSnapshot();
          this.activeModal.close({ updated: true, colors: this.colors, name, themeId: this.themeId });
        }
      },
      error: () => {
        this.saving = false;
        this.saveError = 'Failed to update theme. Please try again.';
      },
    });
  }

  // ── Update + Apply flow (Update Theme button in edit mode) ────────────────

  private _updateAndApply(): void {
    if (this.saving) { return; }
    this.saving = true;
    this.saveError = '';

    const name = this.themeName || this._generateDefaultName();
    const payload: Partial<ProfessorTheme> = Object.assign({}, this.colors, { name });

    this.themeService.updateTheme(this.themeId, payload).subscribe({
      next: () => {
        this.saving = false;
        // Keep colors live — do NOT restore snapshot.
        this.themeService.applyColorsLive(this.colors);
        this.activeModal.close({ applied: true, updated: true, colors: this.colors, name, themeId: this.themeId });
      },
      error: () => {
        this.saving = false;
        this.saveError = 'Failed to update theme. Please try again.';
      },
    });
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  private _generateDefaultName(): string {
    const d = new Date();
    const pad = (n: number) => (n < 10 ? '0' + n : String(n));
    return `Custom ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  resetToDefault(): void {
    this.selectedPresetLabel = 'Ocean Blue';
    const preset = PRESET_THEMES['OCEAN_BLUE'];
    const palette: Partial<ProfessorTheme> = {};
    Object.keys(preset).forEach(key => {
      if (key !== 'label') { palette[key] = preset[key]; }
    });
    Object.assign(this.colors, palette);
    this.colors.buttonColor      = this.colors.primaryButtonColor;
    this.colors.headerBackground = this.colors.primaryButtonColor;
    this.colors.secondaryColor   = this.colors.primaryButtonColor;
    this.themeService.applyColorsLive(this.colors);
    this._syncPreviewVars();
  }

  // ── Sync CSS custom properties to the preview container ───────────────────

  private _syncPreviewVars(): void {
    if (!this.previewRef) { return; }
    const el = this.previewRef.nativeElement;
    el.style.setProperty('--pv-bg',           this.colors.backgroundColor    || '#FFFFFF');
    el.style.setProperty('--pv-text',          this.colors.textColor          || '#111111');
    el.style.setProperty('--pv-header-bg',     this.colors.headerBackground   || '#3737CD');
    el.style.setProperty('--pv-header-text',   this.colors.headerTextColor    || '#FFFFFF');
    el.style.setProperty('--pv-primary',       this.colors.primaryButtonColor || '#3737CD');
    el.style.setProperty('--pv-primary-text',  this.colors.primaryTextColor   || '#FFFFFF');
    el.style.setProperty('--pv-secondary',     this.colors.secondaryColor     || '#1E1E6E');
    el.style.setProperty('--pv-accent',        this.colors.accentColor        || '#7C7CFF');
  }

  cancel(): void {
    this._restoreSnapshot();
    this.activeModal.dismiss();
  }

  // Restores the CSS vars + data attributes that were active before the modal
  // opened. Called on both cancel and save so that closing the modal never
  // silently changes the active theme.
  private _restoreSnapshot(): void {
    const root = document.documentElement;
    this._snapshot.forEach(function(pair) {
      if (pair[1]) {
        root.style.setProperty(pair[0], pair[1]);
      } else {
        root.style.removeProperty(pair[0]);
      }
    });
    if (this._modeSnapshot) {
      root.setAttribute('data-tangram-mode', this._modeSnapshot);
    } else {
      root.removeAttribute('data-tangram-mode');
    }
    if (this._themeAttrSnapshot) {
      root.setAttribute('data-tangram-theme', this._themeAttrSnapshot);
    } else {
      root.removeAttribute('data-tangram-theme');
    }
    if (this._localStorageModeSnapshot !== null) {
      localStorage.setItem('tangram_active_mode', this._localStorageModeSnapshot);
    } else {
      localStorage.removeItem('tangram_active_mode');
    }
  }
}
